import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { TermsModal } from './TermsModal';
import { PrivacyModal } from './PrivacyModal';
interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
// Helper function to parse and format error messages
const getErrorMessage = (data: any, response: Response): string => {
  // Check for API response with CodigoRespuesta and DescripcionRespuesta
  if (data?.CodigoRespuesta || data?.DescripcionRespuesta) {
    const codigo = data.CodigoRespuesta;
    const descripcion = data.DescripcionRespuesta || '';
    // CodigoRespuesta "000" typically means success, anything else is an error
    if (codigo !== '000') {
      const lowerDesc = descripcion.toLowerCase();
      // Check for duplicate/already registered
      if (lowerDesc.includes('ya fue registrado') || lowerDesc.includes('ya registrado') || lowerDesc.includes('ya existe')) {
        return 'Este usuario ya está registrado en el sistema. Si necesitas ayuda, contacta con soporte.';
      }
      // Return the API description if available
      if (descripcion) {
        return descripcion;
      }
      // Fallback based on error code
      return `Error ${codigo}: No se pudo completar el registro.`;
    }
  }
  // Check for other common error message fields
  const apiMessage = data?.message || data?.error || data?.mensaje || data?.descripcion;
  if (apiMessage) {
    const lowerMessage = apiMessage.toLowerCase();
    // Check for duplicate/already registered errors
    if (lowerMessage.includes('ya existe') || lowerMessage.includes('ya registrado') || lowerMessage.includes('duplicado') || lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
      return 'Este usuario ya está registrado. Si olvidaste tu contraseña, contacta con soporte.';
    }
    // Check for invalid data errors
    if (lowerMessage.includes('inválido') || lowerMessage.includes('invalid') || lowerMessage.includes('formato incorrecto')) {
      return `Datos inválidos: ${apiMessage}`;
    }
    // Check for missing required fields
    if (lowerMessage.includes('requerido') || lowerMessage.includes('required') || lowerMessage.includes('obligatorio')) {
      return `Campo requerido: ${apiMessage}`;
    }
    // Return the original API message if it's user-friendly
    if (apiMessage.length < 150) {
      return apiMessage;
    }
  }
  // HTTP status-based messages
  if (response.status === 400) {
    return 'Los datos enviados son incorrectos. Por favor verifica la información.';
  }
  if (response.status === 409) {
    return 'Este usuario ya está registrado en el sistema.';
  }
  if (response.status === 500) {
    return 'Error en el servidor. Por favor intenta nuevamente en unos minutos.';
  }
  if (response.status === 503) {
    return 'El servicio no está disponible temporalmente. Intenta más tarde.';
  }
  // Default error message
  return 'Hubo un error al procesar tu registro. Por favor intenta nuevamente o contacta con soporte.';
};
export function RegistrationModal({
  isOpen,
  onClose
}: RegistrationModalProps) {
  const [idType, setIdType] = useState<'cedula' | 'pasaporte'>('cedula');
  const [formData, setFormData] = useState({
    idNumber: '',
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    acceptTerms: false,
    isAdult: false
  });
  const [idNumberError, setIdNumberError] = useState('');
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  // API integration states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Validate ID number
  const validateIdNumber = (value: string, type: 'cedula' | 'pasaporte'): string => {
    if (!value) return '';
    if (type === 'cedula') {
      // Cedula: exactly 10 numeric digits
      if (!/^\d{10}$/.test(value)) {
        return 'La cédula debe tener exactamente 10 dígitos numéricos';
      }
    } else {
      // Passport: 3-13 alphanumeric characters
      if (!/^[A-Za-z0-9]{3,13}$/.test(value)) {
        return 'El pasaporte debe tener entre 3 y 13 caracteres alfanuméricos';
      }
    }
    return '';
  };
  // Handle ID number change
  const handleIdNumberChange = (value: string) => {
    setFormData({
      ...formData,
      idNumber: value
    });
    const error = validateIdNumber(value, idType);
    setIdNumberError(error);
  };
  // Reset ID number when type changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      idNumber: ''
    }));
    setIdNumberError('');
  }, [idType]);
  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isTermsOpen && !isPrivacyOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isTermsOpen, isPrivacyOpen, onClose]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Final validation check
    const error = validateIdNumber(formData.idNumber, idType);
    if (error) {
      setIdNumberError(error);
      return;
    }
    setIsSubmitting(true);
    setShowError(false);
    try {
      const response = await fetch('https://admin.benefika.net/webservice/consumidor/registro', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authentication: '1aaefcca659e07cb7e1f3411649109b0768006a074ba9f586c2283992c89b593'
        },
        body: JSON.stringify({
          TipoIdentificacion: idType === 'cedula' ? 'CEDULA' : 'PASAPORTE',
          Cedula: formData.idNumber,
          Nombre: formData.firstName,
          Apellido: formData.lastName,
          Email: formData.email,
          Telefono: formData.phone
        })
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Error al procesar la respuesta del servidor.');
      }
      // Log the response for debugging
      console.log('API Response:', {
        status: response.status,
        data
      });
      // Check for errors based on CodigoRespuesta
      // "000" = success, anything else = error
      const isSuccess = data.CodigoRespuesta === '000';
      if (!isSuccess) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Additional fallback checks for other error formats
      if (!response.ok || data.error || data.status === 'error' || data.success === false) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Show success message
      setShowSuccess(true);
      // Reset form
      setFormData({
        idNumber: '',
        lastName: '',
        firstName: '',
        phone: '',
        email: '',
        acceptTerms: false,
        isAdult: false
      });
      setIdNumberError('');
      setIdType('cedula');
      // Auto close after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error al registrar:', error);
      const message = error instanceof Error ? error.message : 'Hubo un error al procesar tu registro. Por favor intenta nuevamente.';
      setErrorMessage(message);
      setShowError(true);
      // Auto hide error after 8 seconds
      setTimeout(() => {
        setShowError(false);
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const isFormValid = formData.idNumber && !idNumberError && formData.lastName && formData.firstName && formData.phone && formData.email && formData.acceptTerms && formData.isAdult;
  return <>
      <AnimatePresence data-id="element-256">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} data-id="element-257" />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-258">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1]
          }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col relative overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-259">
                {/* Error Message */}
                <AnimatePresence data-id="element-260">
                  {showError && <motion.div initial={{
                opacity: 0,
                y: -20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} className="absolute top-0 left-0 right-0 z-40 p-4 bg-red-500 text-white" data-id="element-261">
                      <div className="flex items-start gap-3" data-id="element-262">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-263" />
                        <div className="flex-1" data-id="element-264">
                          <h4 className="font-semibold mb-1" data-id="element-265">
                            Error en el registro
                          </h4>
                          <p className="text-sm" data-id="element-266">{errorMessage}</p>
                        </div>
                        <button onClick={() => setShowError(false)} className="text-white/80 hover:text-white transition-colors" data-id="element-267">
                          <X className="w-5 h-5" data-id="element-268" />
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Success Message */}
                <AnimatePresence data-id="element-269">
                  {showSuccess && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="absolute inset-0 bg-[#2C3E50]/95 backdrop-blur-lg z-50 flex flex-col items-center justify-center p-8" data-id="element-270">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.2,
                  type: 'spring',
                  stiffness: 200
                }} data-id="element-271">
                        <CheckCircle className="w-20 h-20 text-[#5B9BD5]" data-id="element-272" />
                      </motion.div>
                      <motion.h3 initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.3
                }} className="text-3xl font-bold text-white mb-3 text-center mt-6" data-id="element-273">
                        ¡Registro Exitoso!
                      </motion.h3>
                      <motion.p initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} className="text-xl text-[#5B9BD5] text-center" data-id="element-274">
                        Bienvenido a Emprende Fullpass
                      </motion.p>
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="mt-8 flex gap-2" data-id="element-275">
                        <div className="w-2 h-2 bg-[#5B9BD5] rounded-full animate-pulse" style={{
                    animationDelay: '0s'
                  }} data-id="element-276" />
                        <div className="w-2 h-2 bg-[#5B9BD5] rounded-full animate-pulse" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-277" />
                        <div className="w-2 h-2 bg-[#5B9BD5] rounded-full animate-pulse" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-278" />
                      </motion.div>
                    </motion.div>}
                </AnimatePresence>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-279">
                  <div data-id="element-280">
                    <h2 className="text-2xl font-bold text-[#2C3E50]" data-id="element-281">
                      Obtener mi tarjeta
                    </h2>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-282">
                      Completa el formulario para recibir tu Emprende Fullpass
                    </p>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Cerrar" data-id="element-283">
                    <X className="w-5 h-5 text-gray-500" data-id="element-284" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-6" data-id="element-285">
                  {/* ID Type Selection */}
                  <div data-id="element-286">
                    <label className="block text-sm font-semibold text-[#2C3E50] mb-3" data-id="element-287">
                      Tipo de identificación
                    </label>
                    <div className="flex gap-6" data-id="element-288">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-289">
                        <input type="radio" name="idType" value="cedula" checked={idType === 'cedula'} onChange={e => setIdType(e.target.value as 'cedula')} className="w-4 h-4 text-[#5B9BD5] focus:ring-[#5B9BD5] focus:ring-2" data-id="element-290" />
                        <span className="text-gray-700" data-id="element-291">Cédula</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-292">
                        <input type="radio" name="idType" value="pasaporte" checked={idType === 'pasaporte'} onChange={e => setIdType(e.target.value as 'pasaporte')} className="w-4 h-4 text-[#5B9BD5] focus:ring-[#5B9BD5] focus:ring-2" data-id="element-293" />
                        <span className="text-gray-700" data-id="element-294">Pasaporte</span>
                      </label>
                    </div>
                  </div>

                  {/* ID Number */}
                  <div data-id="element-295">
                    <label htmlFor="idNumber" className="block text-sm font-semibold text-[#2C3E50] mb-2" data-id="element-296">
                      {idType === 'cedula' ? 'Cédula' : 'Pasaporte'}
                    </label>
                    <input type="text" id="idNumber" value={formData.idNumber} onChange={e => handleIdNumberChange(e.target.value)} placeholder={idType === 'cedula' ? 'Ej: 1234567890' : 'Ej: ABC123456'} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent outline-none transition-all ${idNumberError ? 'border-red-500' : 'border-gray-300'}`} required disabled={isSubmitting} data-id="element-297" />
                    {idNumberError && <p className="mt-2 text-sm text-red-600" data-id="element-298">
                        {idNumberError}
                      </p>}
                    {!idNumberError && formData.idNumber && <p className="mt-2 text-sm text-gray-500" data-id="element-299">
                        {idType === 'cedula' ? '10 dígitos numéricos' : '3-13 caracteres alfanuméricos'}
                      </p>}
                  </div>

                  {/* Last Name */}
                  <div data-id="element-300">
                    <label htmlFor="lastName" className="block text-sm font-semibold text-[#2C3E50] mb-2" data-id="element-301">
                      Apellido/s
                    </label>
                    <input type="text" id="lastName" value={formData.lastName} onChange={e => setFormData({
                  ...formData,
                  lastName: e.target.value
                })} placeholder="Ingresa tus apellidos" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent outline-none transition-all" required disabled={isSubmitting} data-id="element-302" />
                  </div>

                  {/* First Name */}
                  <div data-id="element-303">
                    <label htmlFor="firstName" className="block text-sm font-semibold text-[#2C3E50] mb-2" data-id="element-304">
                      Nombre/s
                    </label>
                    <input type="text" id="firstName" value={formData.firstName} onChange={e => setFormData({
                  ...formData,
                  firstName: e.target.value
                })} placeholder="Ingresa tus nombres" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent outline-none transition-all" required disabled={isSubmitting} data-id="element-305" />
                  </div>

                  {/* Phone */}
                  <div data-id="element-306">
                    <label htmlFor="phone" className="block text-sm font-semibold text-[#2C3E50] mb-2" data-id="element-307">
                      Teléfono
                    </label>
                    <div className="flex gap-2" data-id="element-308">
                      <div className="flex items-center px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 font-medium" data-id="element-309">
                        +593
                      </div>
                      <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} placeholder="987654321" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent outline-none transition-all" required disabled={isSubmitting} data-id="element-310" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-311">
                    <label htmlFor="email" className="block text-sm font-semibold text-[#2C3E50] mb-2" data-id="element-312">
                      Email
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} placeholder="tu@email.com" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent outline-none transition-all" required disabled={isSubmitting} data-id="element-313" />
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3 pt-2" data-id="element-314">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-315">
                      <input type="checkbox" checked={formData.isAdult} onChange={e => setFormData({
                    ...formData,
                    isAdult: e.target.checked
                  })} className="w-5 h-5 mt-0.5 text-[#5B9BD5] rounded focus:ring-[#5B9BD5] focus:ring-2" required disabled={isSubmitting} data-id="element-316" />
                      <span className="text-sm text-gray-700" data-id="element-317">
                        Soy mayor de 18 años
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-318">
                      <input type="checkbox" checked={formData.acceptTerms} onChange={e => setFormData({
                    ...formData,
                    acceptTerms: e.target.checked
                  })} className="w-5 h-5 mt-0.5 text-[#5B9BD5] rounded focus:ring-[#5B9BD5] focus:ring-2" required disabled={isSubmitting} data-id="element-319" />
                      <span className="text-sm text-gray-700" data-id="element-320">
                        Acepto las{' '}
                        <button type="button" onClick={() => setIsPrivacyOpen(true)} className="text-[#5B9BD5] hover:underline font-medium" data-id="element-321">
                          Políticas de Privacidad
                        </button>{' '}
                        y los{' '}
                        <button type="button" onClick={() => setIsTermsOpen(true)} className="text-[#5B9BD5] hover:underline font-medium" data-id="element-322">
                          Términos y Condiciones
                        </button>
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button type="submit" disabled={!isFormValid || isSubmitting} className="w-full bg-[#5B9BD5] text-white px-6 py-4 rounded-lg font-semibold hover:bg-[#4A8AC4] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed mt-6" data-id="element-323">
                    {isSubmitting ? 'Procesando...' : 'Suscribirme'}
                  </button>
                </form>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} data-id="element-324" />
      <PrivacyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} data-id="element-325" />
    </>;
}